import classNames from 'classnames'
import { useEffect, useRef } from 'react'

import { Button } from 'core-components/library/button'
import { PopupContext } from 'core-components/library/popup'
import { IPopupContext } from 'core-components/library/popup/wrapper/Context'

import { useFocusReturn } from './hooks/useFocusReturn'
import { useFocusTrap } from './hooks/useFocusTrap'
import { IModalPaperProps } from './models/IModalPaperProps'

import styles from './ModalPaper.module.scss'

export const ModalPaper = ({
  className,
  classes,
  children,
  closeModal,
  size,
  title,
  header,
  footer,
  closeOnOutsideClick = true,
  isContainerRestricted = true,
  finishOperationsInProgress,
  popupAttributeData = {},
}: IModalPaperProps) => {
  const windowRef = useRef<HTMLDivElement>(null)
  const unregisterRef = useRef<IPopupContext['unregister'] | null>(null)

  useFocusReturn()
  const handleKeyDown = useFocusTrap()

  const isFullscreen = size === 'fullscreen'

  useEffect(() => {
    return () => {
      unregisterRef.current?.(closeModal)
    }
  }, [])

  useEffect(() => {
    windowRef.current?.focus()
  }, [windowRef])

  return (
    <PopupContext.Consumer>
      {(modalContext: IPopupContext) => {
        modalContext.register(closeModal)
        unregisterRef.current = modalContext.unregister
        return (
          <div
            data-testid={'modal_papper_root'}
            className={classNames(
              styles.overlay,
              {
                [styles['overlay--fullscreen']]: isFullscreen,
              },
              classes?.overlay
            )}
            onMouseDown={
              closeOnOutsideClick && !finishOperationsInProgress ? closeModal : undefined
            }
            {...popupAttributeData}
          >
            <div
              ref={windowRef}
              className={classNames(
                styles.window,
                {
                  [styles[`window--${size}`]]: size,
                },
                classes?.window
              )}
              data-testid={'modal_papper_window'}
              role={'dialog'}
              aria-modal
              tabIndex={-1}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={handleKeyDown}
            >
              <div
                data-testid={'modal_papper_header'}
                className={classNames(
                  styles.header,
                  classes?.header,
                  title || header ? styles['header--padding'] : ''
                )}
              >
                {title || header ? (
                  <div
                    data-testid={'modal_papper_header_body'}
                    className={classNames(styles.headerBody, classes?.headerBody)}
                  >
                    {title ? (
                      <span
                        data-testid={'modal_papper_header_body_title'}
                        className={classNames(styles.headerBody__title, classes?.headerBodyTitle)}
                      >
                        {title}
                      </span>
                    ) : null}
                    {header ? (
                      <div
                        data-testid={'modal_papper_header_body_content'}
                        className={classNames(styles.headerBody__cnt, classes?.headerBodyContent)}
                      >
                        {header()}
                      </div>
                    ) : null}
                  </div>
                ) : null}
                <Button
                  __testId={'ModalDialog__close-button'}
                  className={classNames(
                    styles.close,
                    {
                      [styles['close--fullscreen']]: isFullscreen,
                    },
                    classes?.closeButton
                  )}
                  color={'grey'}
                  variant={'text'}
                  iconButton={'kbase kbase-close'}
                  loading={finishOperationsInProgress}
                  onClick={finishOperationsInProgress ? undefined : closeModal}
                />
              </div>
              <div
                data-testid={'modal_papper_body'}
                className={classNames(
                  className,
                  classes?.body,
                  styles.body,
                  title || header ? styles['body--no-pt'] : '',
                  footer ? styles['body--no-pb'] : '',
                  'scroll-anchor',
                  isContainerRestricted ? 'content-overflow' : ''
                )}
              >
                {children}
              </div>
              {footer ? (
                <div
                  data-testid={'modal_papper_footer'}
                  className={classNames(styles.footer, classes?.footer)}
                >
                  {footer()}
                </div>
              ) : null}
            </div>
          </div>
        )
      }}
    </PopupContext.Consumer>
  )
}
